import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { taskApi } from '../../services/task';
import { TaskInfo, TaskProgress, TaskStatistics, TaskType, GpuActiveStatistics, FilterParams, MonitoringTask, TaskListResponse } from '../../types';

interface TaskState {
  taskList: MonitoringTask[];
  taskStatistics: TaskStatistics | null;
  gpuActiveStatistics: GpuActiveStatistics | null;
  taskTypes: TaskType[];
  statisticsLoading: boolean;
  listLoading: boolean;
  taskTypesLoading: boolean;
  gpuActiveLoading: boolean;
  error: string | null;
  selectedTask: TaskInfo | null;
  taskProgress: TaskProgress | null;
  pagination: {
    page: number;
    size: number;
    total: number;
    hasNext: boolean;
  };
  filters: FilterParams;
}

const initialState: TaskState = {
  taskList: [],
  taskStatistics: null,
  gpuActiveStatistics: null,
  taskTypes: [],
  statisticsLoading: false,
  listLoading: false,
  taskTypesLoading: false,
  gpuActiveLoading: false,
  error: null,
  selectedTask: null,
  taskProgress: null,
  pagination: {
    page: 1,
    size: 10,
    total: 0,
    hasNext: false,
  },
  filters: {},
};

// 异步actions
// 获取任务统计信息（新接口）
export const fetchTaskStatistics = createAsyncThunk(
  'task/fetchTaskStatistics',
  async (params: { startTime: number; endTime: number; priority?: number }, { rejectWithValue }) => {
    try {
      const response = await taskApi.getTaskStatistics(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务统计失败');
    }
  }
);

// 获取任务类型列表
export const fetchTaskTypes = createAsyncThunk(
  'task/fetchTaskTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await taskApi.getTaskTypes();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务类型失败');
    }
  }
);

// 获取GPU活跃统计
export const fetchGpuActiveStatistics = createAsyncThunk(
  'task/fetchGpuActiveStatistics',
  async (params: { startTime: number; endTime: number; priority?: number }, { rejectWithValue }) => {
    try {
      const response = await taskApi.getGpuActiveStatistics(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取GPU活跃统计失败');
    }
  }
);

export const fetchTaskList = createAsyncThunk<TaskListResponse, {
  startTime: number;
  endTime: number;
  taskId?: string | null;
  status?: string;
  taskType?: number | null;
  page: number;
  size: number;
  userId?: string | null;
} | undefined>(
  'task/fetchTaskList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await taskApi.getMonitoringTaskList(params || {
        startTime: Date.now() - 10 * 60 * 1000,
        endTime: Date.now(),
        taskId: null,
        status: '',
        taskType: null,
        page: 1,
        size: 10,
        userId: null,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务列表失败');
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  'task/fetchTaskById',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await taskApi.getTaskById(taskId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务详情失败');
    }
  }
);

export const fetchTaskProgress = createAsyncThunk(
  'task/fetchTaskProgress',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await taskApi.getTaskProgress(taskId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取任务进度失败');
    }
  }
);

export const createTask = createAsyncThunk(
  'task/createTask',
  async (taskData: {
    taskType: number;
    userId: string;
    app: string;
    priority: number;
    payload: string;
    env?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await taskApi.createTask(taskData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '创建任务失败');
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  'task/updateTaskStatus',
  async ({ taskId, statusData }: {
    taskId: string;
    statusData: { status: string; gpuId?: string; message?: string }
  }, { rejectWithValue }) => {
    try {
      const response = await taskApi.updateTaskStatus(taskId, statusData);
      return { taskId, statusData, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error.message || '更新任务状态失败');
    }
  }
);

const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    setSelectedTask: (state, action: PayloadAction<TaskInfo | null>) => {
      state.selectedTask = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<FilterParams>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action: PayloadAction<Partial<TaskState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 任务统计相关
      .addCase(fetchTaskStatistics.pending, (state) => {
        state.statisticsLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.taskStatistics = action.payload;
      })
      .addCase(fetchTaskStatistics.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.error = action.payload as string;
      })
      // 任务类型相关
      .addCase(fetchTaskTypes.pending, (state) => {
        state.taskTypesLoading = true;
      })
      .addCase(fetchTaskTypes.fulfilled, (state, action) => {
        state.taskTypesLoading = false;
        state.taskTypes = action.payload;
      })
      .addCase(fetchTaskTypes.rejected, (state, action) => {
        state.taskTypesLoading = false;
        state.error = action.payload as string;
      })
      // GPU活跃统计相关
      .addCase(fetchGpuActiveStatistics.pending, (state) => {
        state.gpuActiveLoading = true;
        state.error = null;
      })
      .addCase(fetchGpuActiveStatistics.fulfilled, (state, action) => {
        state.gpuActiveLoading = false;
        state.gpuActiveStatistics = action.payload;
      })
      .addCase(fetchGpuActiveStatistics.rejected, (state, action) => {
        state.gpuActiveLoading = false;
        state.error = action.payload as string;
      })
      // 任务列表相关
      .addCase(fetchTaskList.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskList.fulfilled, (state, action) => {
        state.listLoading = false;
        state.taskList = action.payload.records;
        state.pagination = {
          page: action.payload.current,
          size: action.payload.size,
          total: action.payload.total,
          hasNext: action.payload.hasNext,
        };
      })
      .addCase(fetchTaskList.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.selectedTask = action.payload;
      })
      .addCase(fetchTaskProgress.fulfilled, (state, action) => {
        state.taskProgress = action.payload;
      })
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        const { taskId, statusData } = action.payload;
        const task = state.taskList.find(t => t.taskId === taskId);
        if (task) {
          task.status = statusData.status as any;
          task.gpuId = statusData.gpuId;
          task.updatedAt = Date.now();
        }
      });
  },
});

export const { setSelectedTask, setFilters, setPagination, clearError } = taskSlice.actions;
export default taskSlice.reducer;
